import { contractTypesQueryOptions } from '@/shared/contract-types-query';
import type { PatchJobsIdMutationRequest } from '@eureca/recruiter-api-sdk/types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast, type ComboboxOption } from '@eureca/ui';
import {
  revalidateLogic,
  useDebouncedValue,
  useForm,
  useFormErrors,
  useStore,
} from '@eureca/ui/forms';
import { z } from 'zod';
import { JobEditConditionsPage as JobEditConditionsPageComponent } from './JobEditConditionsPage.component';
import type {
  JobEditConditionsValues,
  RelocationScope,
  YesNo,
} from './JobEditConditionsPage.component';
import { emptyValues } from './JobEditConditionsPage.mock';
import { BRAZILIAN_UF_OPTIONS } from './brazilian-ufs';
import {
  citiesByStatesQueryOptions,
  citiesSearchQueryOptions,
  cityWithStateLabel,
  type City,
} from './cities-query';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '@/features/jobs/job-shared/job-status';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';
import { JobMutationError, usePatchJob } from '@/features/jobs/job-shared/job-edit-mutation';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';

type WorkModel = 'hibrido' | 'presencial' | 'remoto';
// EU-497 — Turno inclui `integral`.
type Shift = 'matinal' | 'vespertino' | 'noturno' | 'integral';
type WeeklyHours = 15 | 20 | 30 | 40 | 44;

/**
 * Campos da seção que podem ganhar a marca de opcional. Não é a lista de "o que
 * é opcional" (essa vem da API) — é o inventário do que a TELA sabe desenhar,
 * usado para traduzir o `requiredFields` do contrato em dicas por campo.
 */
const WORK_CONDITION_FIELDS = [
  'workModels',
  'requirementStates',
  'requirementCities',
  'workplaceCities',
  'relocationAvailability',
  'relocationScopes',
  'travelAvailability',
  'travelScopes',
  'shifts',
  'weeklyHoursList',
] as const;

const scopeOption = z.union([z.literal('dentro-do-brasil'), z.literal('fora-do-brasil')]);

const schema = z.object({
  // EU-709 RN-11 — num Banco de talentos o `Tipo de vaga` é o ÚNICO campo
  // obrigatório da seção; nos demais tipos ele também é exigido para salvar
  // (é o campo que declara o tipo). O Core recusa limpá-lo com 422 (BDT-013),
  // então a validação aqui é o mesmo gate, antecipado para a tela.
  contractType: z.string().min(1, 'Selecione o tipo de vaga'),
  // EU-497 — Condições de trabalho múltipla escolha (arrays de tokens).
  workModels: z.array(z.string()),
  // Juliana QA bug — EU-160. Multi-select arrays.
  requirementStates: z.array(z.string()),
  requirementCities: z.array(z.string()),
  workplaceCities: z.array(z.string()),
  relocationAvailability: z.union([z.literal('sim'), z.literal('nao'), z.literal('')]),
  relocationScope: z.array(scopeOption),
  travelAvailability: z.union([z.literal('sim'), z.literal('nao'), z.literal('')]),
  travelScope: z.array(scopeOption),
  shifts: z.array(z.string()),
  weeklyHoursList: z.array(z.string()),
});

function yesNoToBool(value: YesNo | string): boolean | null {
  if (value === 'sim') return true;
  if (value === 'nao') return false;
  return null;
}

function boolToYesNo(value: boolean | null | undefined): YesNo {
  if (value === true) return 'sim';
  if (value === false) return 'nao';
  return '';
}

/**
 * EU-709 D-02 — token PT-BR do tipo. Vem do catálogo do BFF, a MESMA origem do
 * enum do SDK, então a lista não tem como divergir; o alias estreito fecha a
 * fronteira SPA→SDK sem afrouxar o tipo do formulário, que precisa de `string`
 * para representar "nada selecionado ainda".
 */
type JobContractTypeToken = NonNullable<PatchJobsIdMutationRequest['contractType']>;

interface PatchPayload {
  contractType?: JobContractTypeToken;
  // EU-497 — Condições de trabalho múltipla escolha. O Core espelha o escalar
  // (`workModel`/`shift`/`weeklyHours`) como o primeiro selecionado, então o
  // SPA envia apenas os arrays.
  workModels?: WorkModel[];
  requirementStates?: string[];
  requirementCities?: string[];
  workplaceCities?: string[];
  relocationAvailability?: boolean | null;
  relocationScopes?: RelocationScope[];
  travelAvailability?: boolean | null;
  travelScopes?: RelocationScope[];
  shifts?: Shift[];
  weeklyHoursList?: WeeklyHours[];
}

// Cada campo é independentemente opcional no Core. EU-497 — sem special-case de
// Remoto: os arrays de localidade são replace-set verbatim (RN-04, Local
// independente dos requisitos). A Carga (`'30'`) é convertida para número.
function buildPayload(values: JobEditConditionsValues): PatchPayload {
  const payload: PatchPayload = {
    contractType: values.contractType as JobContractTypeToken,
    workModels: values.workModels as WorkModel[],
    requirementStates: values.requirementStates,
    requirementCities: values.requirementCities,
    workplaceCities: values.workplaceCities,
    relocationAvailability: yesNoToBool(values.relocationAvailability),
    relocationScopes: values.relocationScope,
    travelAvailability: yesNoToBool(values.travelAvailability),
    travelScopes: values.travelScope,
    shifts: values.shifts as Shift[],
    weeklyHoursList: values.weeklyHoursList.map((h) => Number(h) as WeeklyHours),
  };
  return payload;
}

export default function JobEditConditionsPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/condicoes-de-trabalho',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const patchJob = usePatchJob(jobId);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  // EU-497 RN-04 — "Local de trabalho" é um AUTOCOMPLETE único de cidade (sem
  // seletor de estado): o consultor digita e busca em todas as UFs; a opção
  // aparece como "Cidade - UF" pra desambiguar homônimas.
  const [workplaceSearch, setWorkplaceSearch] = useState('');
  const debouncedWorkplaceSearch = useDebouncedValue(workplaceSearch, 300);

  const citiesQuery = useQuery(citiesByStatesQueryOptions(selectedStates));
  const workplaceSearchQuery = useQuery(citiesSearchQueryOptions(debouncedWorkplaceSearch));

  const job = jobQuery.data;
  // EU-569 — vaga Encerrada/Cancelada: seção em modo somente visualização.
  const readOnly = isJobDetailReadOnly(job);
  const jobName = job?.name ?? '';

  // EU-709 RN-01 — os oito tipos, mesma origem do `Tipo de programa`.
  const contractTypesQuery = useQuery(contractTypesQueryOptions());
  const contractTypeOptions = useMemo(
    () =>
      (contractTypesQuery.data ?? [])
        .filter((t): t is typeof t & { token: string } => t.token !== null)
        .map((t) => ({ value: t.token, label: t.label })),
    [contractTypesQuery.data]
  );

  // EU-709 RN-11 / RN-20 — quais campos DESTA seção deixam de ser exigidos sai
  // do contrato que a API resolveu pelo tipo da vaga. Sem contrato (carregando,
  // ou Core antigo) nada é marcado como opcional: a tela mantém as dicas de
  // hoje em vez de anunciar uma opcionalidade que o backend não confirmou.
  const optionalWorkConditionFields = useMemo(() => {
    const required = job?.requirements?.requiredFields?.['work-conditions'];
    if (!required) return [];
    return WORK_CONDITION_FIELDS.filter((field) => !required.includes(field));
  }, [job]);
  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  const cityOptions: ComboboxOption[] = useMemo(() => {
    const items: City[] = citiesQuery.data ?? [];
    return items.map((c) => ({ value: c.name, label: c.name }));
  }, [citiesQuery.data]);

  const workplaceCityOptions: ComboboxOption[] = useMemo(() => {
    const items = workplaceSearchQuery.data ?? [];
    // Valor === rótulo === "Cidade - UF": é o que fica salvo em workplaceCities
    // e o que o candidato exibe (o cityPairs do Core resolve o UF pelo nome).
    return items.map((c) => {
      const label = cityWithStateLabel(c);
      return { value: label, label };
    });
  }, [workplaceSearchQuery.data]);

  async function persist(values: JobEditConditionsValues) {
    try {
      await patchJob.mutateAsync(buildPayload(values));
      toast('Alterações salvas com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      void navigate({
        to: '/vagas/$jobId',
        params: { jobId },
      });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Vaga não encontrada.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para editar essa vaga.');
      } else {
        toast.error('Não foi possível salvar as alterações. Tente novamente.');
      }
    }
  }

  const form = useForm({
    defaultValues: emptyValues satisfies JobEditConditionsValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      await persist(value);
    },
  });

  useEffect(() => {
    if (hasHydrated || !job) return;
    // Juliana QA bug — EU-160. Hydrate from new array fields (with empty
    // fallback for forward-compat: rows persisted before the agent paralelo
    // do Core adicionar as colunas arrivam com `undefined`).
    // EU-497 — hidrata os arrays multi-select do Core (`workModels`/`shifts`/
    // `weeklyHoursList`), com fallback defensivo para o escalar legado (rows
    // persistidas antes das colunas array; o `recruiter-api-sdk` ainda não foi
    // regenerado, então lemos via cast — mesmo padrão dos arrays do EU-160).
    type JobWithLocations = typeof job & {
      requirementStates?: string[] | null;
      requirementCities?: string[] | null;
      workplaceCities?: string[] | null;
      workModels?: string[] | null;
      shifts?: string[] | null;
      weeklyHoursList?: number[] | null;
    };
    const j = job as JobWithLocations;
    const workModels = j.workModels ?? (job.workModel ? [job.workModel] : []);
    const shifts = j.shifts ?? (job.shift ? [job.shift] : []);
    const weeklyHoursList = (
      j.weeklyHoursList ?? (job.weeklyHours != null ? [job.weeklyHours] : [])
    ).map(String);
    const hydrated: JobEditConditionsValues = {
      contractType: job.contractType ?? '',
      workModels: [...workModels],
      requirementStates: [...(j.requirementStates ?? [])],
      requirementCities: [...(j.requirementCities ?? [])],
      workplaceCities: [...(j.workplaceCities ?? [])],
      relocationAvailability: boolToYesNo(job.relocationAvailability),
      relocationScope: [...(job.relocationScopes ?? [])] as RelocationScope[],
      travelAvailability: boolToYesNo(job.travelAvailability),
      travelScope: [...(job.travelScopes ?? [])] as RelocationScope[],
      shifts: [...shifts],
      weeklyHoursList: [...weeklyHoursList],
    };
    form.setFieldValue('contractType', hydrated.contractType);
    form.setFieldValue('workModels', hydrated.workModels);
    form.setFieldValue('requirementStates', hydrated.requirementStates);
    form.setFieldValue('requirementCities', hydrated.requirementCities);
    form.setFieldValue('workplaceCities', hydrated.workplaceCities);
    form.setFieldValue('relocationAvailability', hydrated.relocationAvailability);
    form.setFieldValue('relocationScope', hydrated.relocationScope);
    form.setFieldValue('travelAvailability', hydrated.travelAvailability);
    form.setFieldValue('travelScope', hydrated.travelScope);
    form.setFieldValue('shifts', hydrated.shifts);
    form.setFieldValue('weeklyHoursList', hydrated.weeklyHoursList);
    if (hydrated.requirementStates.length > 0) {
      setSelectedStates(hydrated.requirementStates);
    }
    setHasHydrated(true);
  }, [job, hasHydrated, form]);

  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const errors = useFormErrors(form);

  function handleBack() {
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  function handleFieldChange<K extends keyof JobEditConditionsValues>(
    field: K,
    value: JobEditConditionsValues[K]
  ) {
    form.setFieldValue(field, value as never);

    // EU-497 — sem special-case de Remoto (múltipla escolha): a localidade não
    // é mais limpa ao escolher Remoto (RN-04). Auto-prune de cidades segue.
    // Juliana QA bug — EU-160. Auto-prune cidades órfãs ao remover UF.
    if (field === 'requirementStates') {
      const nextStates = (value as string[]).map((s) => s.toUpperCase());
      setSelectedStates(nextStates);
      // RN-04 — só as cidades de REQUISITO seguem os estados de requisito. O
      // "Local de trabalho" é independente (não é mais limpo aqui).
      if (nextStates.length === 0) {
        form.setFieldValue('requirementCities', [] as string[]);
      }
      // Cidades órfãs (de UFs que saíram da seleção) — não podemos saber
      // qual cidade veio de qual UF apenas via nome, então não fazemos
      // prune granular aqui; o próximo refetch de `citiesByStatesQueryOptions`
      // restringe `cityOptions`. Em vez disso, filtramos cidades selecionadas
      // para manter só as que ainda aparecem em `cityOptions`. Como a query
      // é assíncrona, fazemos prune defensivo somente no caso `nextStates=[]`.
    }
    if (field === 'relocationAvailability' && value !== 'sim') {
      form.setFieldValue('relocationScope', [] as RelocationScope[]);
    }
    if (field === 'travelAvailability' && value !== 'sim') {
      form.setFieldValue('travelScope', [] as RelocationScope[]);
    }
  }

  // Juliana QA bug — EU-160. Quando as cidades de REQUISITO disponíveis
  // (cityOptions) mudam por causa de troca de UFs, filtra a seleção para
  // remover órfãs. Roda DEPOIS do citiesQuery resolver.
  useEffect(() => {
    if (!hasHydrated) return;
    if (citiesQuery.isLoading) return;
    const validNames = new Set(cityOptions.map((o) => o.value));
    if (validNames.size === 0) return;
    const prunedReq = values.requirementCities.filter((c) => validNames.has(c));
    if (prunedReq.length !== values.requirementCities.length) {
      form.setFieldValue('requirementCities', prunedReq);
    }
    // Intentionally only re-runs when the available `cityOptions` change —
    // including `values.*` would re-prune on every setFieldValue.
  }, [cityOptions, hasHydrated, citiesQuery.isLoading]);

  return (
    <JobEditConditionsPageComponent
      jobName={jobName}
      companyLogoUrl={companyLogoUrl}
      companyName={companyName}
      values={values}
      errors={errors}
      isSubmitting={isSubmitting || patchJob.isPending}
      readOnly={readOnly}
      contractTypeOptions={contractTypeOptions}
      optionalFields={optionalWorkConditionFields}
      stateOptions={[...BRAZILIAN_UF_OPTIONS]}
      cityOptions={cityOptions}
      workplaceCityOptions={workplaceCityOptions}
      workplaceSearchLoading={workplaceSearchQuery.isFetching}
      onWorkplaceSearchChange={setWorkplaceSearch}
      onFieldChange={handleFieldChange}
      onBack={handleBack}
      onSubmit={() => {
        // EU-569 — modo visualização: nada persiste.
        if (readOnly) return;
        void form.handleSubmit();
      }}
    />
  );
}
